import { PageHero } from "../components/PageHero";
import { Reveal } from "../components/Reveal";
import { OutlineButton } from "../components/OutlineButton";
import { IMAGES } from "../lib/assets";

const ROLES = [
  {
    title: "Investors",
    text: "We allocate capital with conviction and patience, seeking durable value across businesses, real assets, and markets.",
  },
  {
    title: "Partners",
    text: "We work alongside exceptional operators and families, contributing capital, perspective, and a long-term orientation.",
  },
  {
    title: "Stewards",
    text: "We protect and grow what has been entrusted to us, mindful that legacy is measured across generations.",
  },
];

export default function FamilyOffice() {
  return (
    <div data-testid="family-office-page">
      <PageHero
        testId="family-office-hero"
        eyebrow="Family Office"
        title={
          <>
            Built to
            <br />
            endure.
          </>
        }
        intro="A private, multi-generational family office rooted in Southern California — investing with discipline, patience, and a long-term perspective."
        image={IMAGES.opportunities}
        alt="Modern coastal residence at dusk"
      />

      {/* Statement / two column */}
      <section className="bg-aveden-black grid grid-cols-1 lg:grid-cols-2">
        <div className="flex items-center px-6 sm:px-12 lg:px-20 py-24 lg:py-32">
          <Reveal>
            <div className="max-w-xl">
              <p className="font-sans text-[11px] tracking-[0.4em] uppercase text-aveden-gold mb-8">
                The Office
              </p>
              <h2 className="font-serif font-light text-aveden-white text-4xl lg:text-5xl leading-[1.12] tracking-tight mb-10">
                Capital with a conscience and a calendar measured in decades.
              </h2>
              <div className="space-y-6 font-sans font-light text-aveden-gray text-base leading-relaxed">
                <p>
                  AVEDEN is a private family office investing across public and
                  private markets, real assets, and select opportunities where
                  we can create meaningful, long-term value.
                </p>
                <p>
                  We are deliberately small, deliberately private, and
                  deliberately patient — free to think in generations rather
                  than cycles.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
        <div className="relative min-h-[420px] lg:min-h-[700px] overflow-hidden order-first lg:order-last">
          <img
            src={IMAGES.interior}
            alt="Minimalist office interior overlooking the Pacific"
            className="absolute inset-0 h-full w-full object-cover"
            loading="lazy"
          />
        </div>
      </section>

      {/* Roles */}
      <section className="bg-aveden-rich py-24 lg:py-32 border-t border-white/[0.05]">
        <div className="mx-auto max-w-[1400px] px-6 sm:px-10 lg:px-16">
          <Reveal>
            <h2 className="font-serif font-light text-aveden-white text-3xl lg:text-4xl text-center mb-20">
              Investors. Partners. Stewards.
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-16 lg:gap-20">
            {ROLES.map((r, i) => (
              <Reveal key={r.title} delay={i * 0.1}>
                <div
                  data-testid={`role-${r.title.toLowerCase()}`}
                  className="text-center"
                >
                  <h3 className="font-serif font-light text-aveden-gold text-2xl tracking-wide mb-6">
                    {r.title}
                  </h3>
                  <p className="font-sans font-light text-sm text-aveden-gray leading-relaxed max-w-xs mx-auto">
                    {r.text}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-aveden-black py-28 lg:py-36 text-center">
        <Reveal>
          <h2 className="font-serif font-light text-aveden-white text-3xl lg:text-4xl mb-12">
            For inquiries, we welcome a private conversation.
          </h2>
          <OutlineButton to="/contact" testId="family-office-contact-button">
            Contact
          </OutlineButton>
        </Reveal>
      </section>
    </div>
  );
}
